import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Trophy, Store } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [kind, setKind] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setKind(data.kind);
        if (data.payment_status === "paid") { setStatus("paid"); refresh(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* keep trying */ }
      if (tries++ < 10) setTimeout(poll, 1800);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="card-surface rounded-2xl p-10 max-w-md w-full text-center gold-glow animate-rise" data-testid="checkout-success">
        {status === "checking" && (<><Loader2 className="w-14 h-14 text-gold mx-auto animate-spin" /><h1 className="font-display text-2xl font-bold text-white mt-5">Confirming payment…</h1><p className="text-slate-400 mt-2">Hang tight while we verify with Stripe.</p></>)}
        {status === "paid" && (<>
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />
          <h1 className="font-display text-2xl font-bold text-white mt-5">Payment confirmed!</h1>
          <p className="text-slate-400 mt-2">{kind === "drop" ? "Your seats are locked in. Good luck in the draw!" : "Your order is placed — the host will ship it out soon."}</p>
          <div className="mt-6 flex flex-col gap-3">
            {kind === "drop" ? <Link to="/drops" className="gold-gradient text-obsidian font-bold py-3 rounded-xl flex items-center justify-center gap-2"><Trophy className="w-5 h-5" /> Back to Drops</Link> : <Link to="/orders" className="gold-gradient text-obsidian font-bold py-3 rounded-xl flex items-center justify-center gap-2"><Store className="w-5 h-5" /> View My Orders</Link>}
            <Link to="/" className="text-slate-400 text-sm">Go home</Link>
          </div>
        </>)}
        {(status === "error" || status === "timeout") && (<>
          <XCircle className="w-16 h-16 text-red-400 mx-auto" />
          <h1 className="font-display text-2xl font-bold text-white mt-5">{status === "timeout" ? "Still processing" : "Payment issue"}</h1>
          <p className="text-slate-400 mt-2">{status === "timeout" ? "This is taking longer than usual. Your order will update automatically once confirmed." : "We couldn't confirm this payment. If you were charged, it will be refunded."}</p>
          <Link to="/drops" className="mt-6 inline-block card-surface px-6 py-3 rounded-xl text-white font-bold">Back to Drops</Link>
        </>)}
      </div>
    </div>
  );
}
